/**
 * IncidentSummaryCards Component - Phase 13
 * 
 * KPI summary cards for incident dashboard.
 */

import {
  AlertCircle,
  CheckCircle,
  XCircle,
  AlertTriangle,
  CreditCard,
  RefreshCw,
  MapPin,
  Monitor,
  Clock
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/**
 * Single Summary Card
 */
function SummaryCard({ icon: Icon, label, value, subValue, color, onClick }) {
  return (
    <Card 
      className={cn(
        "cursor-pointer transition-all hover:shadow-md",
        onClick && "hover:border-primary"
      )}
      onClick={onClick}
      data-testid={`summary-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={cn(
            "p-2 rounded-lg",
            color || "bg-gray-100 dark:bg-gray-800"
          )}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
            {subValue && (
              <p className="text-xs text-muted-foreground mt-0.5">{subValue}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * IncidentSummaryCards Component
 */
export function IncidentSummaryCards({ summary, onFilterClick }) {
  if (!summary) return null;

  const handleClick = (filterKey, filterValue) => {
    if (onFilterClick) {
      onFilterClick({ [filterKey]: filterValue });
    }
  };

  return (
    <div 
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3"
      data-testid="incident-summary-cards"
    >
      <SummaryCard
        icon={AlertCircle}
        label="Open Incidents"
        value={summary.open || 0}
        color="bg-blue-100 dark:bg-blue-900/30 text-blue-600"
        onClick={() => handleClick('unresolvedOnly', true)}
      />
      
      <SummaryCard
        icon={CheckCircle}
        label="Resolved"
        value={summary.resolved || 0}
        color="bg-green-100 dark:bg-green-900/30 text-green-600"
        onClick={() => handleClick('status', 'resolved')}
      />
      
      <SummaryCard
        icon={AlertTriangle}
        label="Conflicts"
        value={summary.conflict || 0}
        color="bg-purple-100 dark:bg-purple-900/30 text-purple-600"
        onClick={() => handleClick('status', 'conflict')}
      />
      
      <SummaryCard
        icon={XCircle}
        label="Failed"
        value={summary.failed || 0}
        color="bg-red-100 dark:bg-red-900/30 text-red-600"
        onClick={() => handleClick('status', 'failed')}
      />
      
      <SummaryCard
        icon={AlertTriangle}
        label="Critical"
        value={summary.critical || 0}
        color="bg-red-100 dark:bg-red-900/30 text-red-600"
        onClick={() => handleClick('severity', 'critical')}
      />
      
      <SummaryCard
        icon={CreditCard}
        label="Blocked Payments"
        value={(summary.byDomain?.payments || 0)}
        color="bg-amber-100 dark:bg-amber-900/30 text-amber-600"
        onClick={() => handleClick('domain', 'payments')}
      />
      
      <SummaryCard
        icon={MapPin}
        label="Locations"
        value={summary.locationCount || 0}
        color="bg-gray-100 dark:bg-gray-800 text-gray-600"
      />
      
      <SummaryCard
        icon={Monitor}
        label="Terminals"
        value={summary.terminalCount || 0}
        color="bg-gray-100 dark:bg-gray-800 text-gray-600"
      />
    </div>
  );
}

/**
 * Compact Summary Row
 */
export function IncidentSummaryRow({ summary }) {
  if (!summary) return null;

  return (
    <div className="flex items-center gap-4 text-sm flex-wrap" data-testid="incident-summary-row">
      <div className="flex items-center gap-1">
        <AlertCircle className="h-4 w-4 text-blue-500" />
        <span className="font-medium">{summary.open}</span>
        <span className="text-muted-foreground">open</span>
      </div>
      
      <div className="flex items-center gap-1">
        <XCircle className="h-4 w-4 text-red-500" />
        <span className="font-medium">{summary.failed}</span>
        <span className="text-muted-foreground">failed</span>
      </div>
      
      <div className="flex items-center gap-1">
        <AlertTriangle className="h-4 w-4 text-purple-500" />
        <span className="font-medium">{summary.conflict}</span>
        <span className="text-muted-foreground">conflicts</span>
      </div>
      
      {summary.critical > 0 && (
        <div className="flex items-center gap-1 text-red-600">
          <AlertTriangle className="h-4 w-4" />
          <span className="font-medium">{summary.critical}</span>
          <span>critical</span>
        </div>
      )}
      
      <div className="flex items-center gap-1 text-muted-foreground">
        <Clock className="h-4 w-4" />
        <span>{summary.last24h} in 24h</span>
      </div>
    </div>
  );
}

export default IncidentSummaryCards;
